/**
 * 게시글 상세 컴포넌트
 */

import React from 'react';
import styled from '@emotion/styled';
import { theme } from '../../../styles/theme';
import { mediaQueries } from '../../../constants/breakpoints';
import { BoardPost } from '../../../types/board.types';
import { Button } from '../../UI';

interface BoardDetailProps {
  post: BoardPost;
  onBack: () => void;
}

const DetailContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.md};
  }
`;

const Header = styled.div`
  border-bottom: 2px solid ${theme.colors.border};
  padding-bottom: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

const Meta = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
`;

const Content = styled.div`
  font-size: ${theme.fontSizes.md};
  line-height: 1.8;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xl};
  white-space: pre-wrap;
`;

const ImageGallery = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
`;

const Image = styled.img`
  width: 100%;
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.sm};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border};
`;

export const BoardDetail: React.FC<BoardDetailProps> = ({ post, onBack }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DetailContainer>
      <Header>
        <Title>{post.title}</Title>
        <Meta>
          <span>작성일: {formatDate(post.createdAt)}</span>
          <span>조회수: {post.views}</span>
        </Meta>
      </Header>

      <Content>{post.content}</Content>

      {post.files && post.files.length > 0 && (
        <ImageGallery>
          {post.files.map((file) => (
            <Image key={file.id} src={file.fileUrl} alt={file.originalFileName} />
          ))}
        </ImageGallery>
      )}

      <ButtonContainer>
        <Button onClick={onBack} variant="outline">
          목록으로
        </Button>
      </ButtonContainer>
    </DetailContainer>
  );
};
